<script lang="ts">
  import { onMount } from 'svelte';

  interface Suggestion {
    type: 'post' | 'user';
    id: string;
    text: string;
  }

  interface Props {
    placeholder?: string;
    autoFocus?: boolean;
    onNavigate?: (path: string) => void;
  }

  let { placeholder = '검색어를 입력하세요...', autoFocus = false, onNavigate }: Props = $props();

  let query = $state('');
  let suggestions = $state<Suggestion[]>([]);
  let isLoading = $state(false);
  let showSuggestions = $state(false);
  let selectedIndex = $state(-1);
  let inputRef: HTMLInputElement;
  let debounceTimer: ReturnType<typeof setTimeout>;

  // 디바운스된 자동완성 요청
  function handleInput() {
    clearTimeout(debounceTimer);
    selectedIndex = -1;

    if (query.length < 2) {
      suggestions = [];
      showSuggestions = false;
      return;
    }

    debounceTimer = setTimeout(async () => {
      isLoading = true;
      try {
        const response = await fetch(`/api/search/autocomplete?q=${encodeURIComponent(query)}`);
        const data = await response.json();
        suggestions = data.suggestions || [];
        showSuggestions = suggestions.length > 0;
      } catch (_error) {
        // Silently handle autocomplete errors
        suggestions = [];
      } finally {
        isLoading = false;
      }
    }, 200);
  }

  // 키보드 네비게이션
  function handleKeydown(event: KeyboardEvent) {
    if (!showSuggestions) {
      if (event.key === 'Enter' && query.trim()) {
        performSearch();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        selectedIndex = Math.min(selectedIndex + 1, suggestions.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        selectedIndex = Math.max(selectedIndex - 1, -1);
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0) {
          selectSuggestion(suggestions[selectedIndex]);
        } else {
          performSearch();
        }
        break;
      case 'Escape':
        showSuggestions = false;
        selectedIndex = -1;
        break;
    }
  }

  // 검색 실행
  function performSearch() {
    if (!query.trim()) return;
    showSuggestions = false;
    onNavigate?.(`/search?q=${encodeURIComponent(query.trim())}`);
  }

  // 제안 선택
  function selectSuggestion(suggestion: Suggestion) {
    showSuggestions = false;

    if (suggestion.type === 'post') {
      onNavigate?.(`/posts/${suggestion.id}`);
    } else if (suggestion.type === 'user') {
      onNavigate?.(`/users/${suggestion.id}`);
    }
  }

  // 포커스 잃으면 제안 숨김 (딜레이)
  function handleBlur() {
    setTimeout(() => {
      showSuggestions = false;
    }, 200);
  }

  onMount(() => {
    if (autoFocus && inputRef) {
      inputRef.focus();
    }
  });
</script>

<div class="relative w-full max-w-xl">
  <div class="relative">
    <!-- 검색 아이콘 -->
    <svg 
      class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24"
    >
      <path 
        stroke-linecap="round" 
        stroke-linejoin="round" 
        stroke-width="2" 
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>

    <!-- 검색 입력 -->
    <input
      bind:this={inputRef}
      bind:value={query}
      type="text"
      {placeholder}
      class="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg
             focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
             transition-colors"
      oninput={handleInput}
      onkeydown={handleKeydown}
      onfocus={() => query.length >= 2 && (showSuggestions = suggestions.length > 0)}
      onblur={handleBlur}
    />

    <!-- 로딩 인디케이터 -->
    {#if isLoading}
      <div class="absolute right-3 top-1/2 -translate-y-1/2">
        <svg class="animate-spin w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
      </div>
    {:else if query}
      <button
        type="button"
        class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        onclick={() => { query = ''; suggestions = []; }}
        aria-label="검색어 지우기"
        title="검색어 지우기"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    {/if}
  </div>

  <!-- 자동완성 드롭다운 -->
  {#if showSuggestions && suggestions.length > 0}
    <div 
      class="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 
             rounded-lg shadow-lg z-50 overflow-hidden"
    >
      {#each suggestions as suggestion, index (suggestion.id)}
        <button
          type="button"
          class="w-full px-4 py-2.5 text-left flex items-center gap-3 
                 hover:bg-gray-50 transition-colors
                 {index === selectedIndex ? 'bg-indigo-50' : ''}"
          onclick={() => selectSuggestion(suggestion)}
          onmouseenter={() => selectedIndex = index}
        >
          <!-- 타입 아이콘 -->
          {#if suggestion.type === 'post'}
            <span class="text-gray-400">📄</span>
          {:else}
            <span class="text-gray-400">👤</span>
          {/if}
          
          <span class="flex-1 truncate">{suggestion.text}</span>
          
          <span class="text-xs text-gray-400">
            {suggestion.type === 'post' ? '게시글' : '사용자'}
          </span>
        </button>
      {/each}

      <!-- 전체 검색 링크 -->
      <button
        type="button"
        class="w-full px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50 
               border-t border-gray-100 text-left"
        onclick={performSearch}
      >
        "{query}" 전체 검색 →
      </button>
    </div>
  {/if}
</div>
